import { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  useSites,
  useApproveSite,
  useRejectSite,
  useDeleteSite,
  usePluginInventory,
  siteKeys,
  type Site,
} from '../hooks/useSites'
import { useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/client/lib/api-client'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { toast } from 'sonner'
import { Input } from '@/components/ui/input'
import {
  Globe,
  CheckCircle,
  XCircle,
  Clock,
  MoreHorizontal,
  Trash2,
  ExternalLink,
  Download,
  AlertTriangle,
  Search,
  Package,
  X,
  ArrowUpCircle,
  Loader2,
  ScanSearch,
} from 'lucide-react'
import { differenceInDays } from 'date-fns'

/**
 * Check if site is inactive (no activity in X days)
 */
function getInactivityStatus(lastActivityAt: string | null): 'active' | 'warning' | 'critical' | null {
  if (!lastActivityAt) return 'critical' // Never sent logs
  const daysSinceActivity = differenceInDays(new Date(), new Date(lastActivityAt))
  if (daysSinceActivity >= 30) return 'critical'
  if (daysSinceActivity >= 7) return 'warning'
  return 'active'
}

function SiteActivityBadge({ lastActivityAt, status }: { lastActivityAt: string | null; status: Site['status'] }) {
  // Only show for approved sites
  if (status !== 'approved') return null

  const inactivity = getInactivityStatus(lastActivityAt)

  if (inactivity === 'critical') {
    return (
      <Badge variant="destructive" className="flex items-center w-fit text-xs">
        <AlertTriangle className="h-3 w-3 mr-1" />
        {lastActivityAt ? '30+ days' : 'Never'}
      </Badge>
    )
  }

  if (inactivity === 'warning') {
    return (
      <Badge variant="outline" className="flex items-center w-fit text-xs border-yellow-500 text-yellow-600 dark:text-yellow-400">
        <Clock className="h-3 w-3 mr-1" />
        7+ days
      </Badge>
    )
  }

  return null
}

function SiteStatusBadge({ status }: { status: Site['status'] }) {
  const variants: Record<Site['status'], { variant: 'default' | 'secondary' | 'destructive'; label: string; icon: React.ReactNode }> = {
    approved: { variant: 'default', label: 'Approved', icon: <CheckCircle className="h-3 w-3 mr-1" /> },
    pending: { variant: 'secondary', label: 'Pending', icon: <Clock className="h-3 w-3 mr-1" /> },
    rejected: { variant: 'destructive', label: 'Rejected', icon: <XCircle className="h-3 w-3 mr-1" /> },
  }
  const { variant, label, icon } = variants[status]
  return (
    <Badge variant={variant} className="flex items-center w-fit">
      {icon}
      {label}
    </Badge>
  )
}

function SiteActions({
  site,
  onApprove,
  onReject,
  onDelete,
}: {
  site: Site
  onApprove: (domain: string) => void
  onReject: (domain: string) => void
  onDelete: (domain: string) => void
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon">
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem asChild>
          <a
            href={`https://${site.domain}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center"
          >
            <ExternalLink className="h-4 w-4 mr-2" />
            Visit Site
          </a>
        </DropdownMenuItem>
        {site.status === 'pending' && (
          <>
            <DropdownMenuItem onClick={() => onApprove(site.domain)}>
              <CheckCircle className="h-4 w-4 mr-2 text-green-500" />
              Approve
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onReject(site.domain)}>
              <XCircle className="h-4 w-4 mr-2 text-destructive" />
              Reject
            </DropdownMenuItem>
          </>
        )}
        {site.status === 'rejected' && (
          <DropdownMenuItem onClick={() => onApprove(site.domain)}>
            <CheckCircle className="h-4 w-4 mr-2 text-green-500" />
            Approve
          </DropdownMenuItem>
        )}
        {site.status === 'approved' && (
          <DropdownMenuItem onClick={() => onReject(site.domain)}>
            <XCircle className="h-4 w-4 mr-2 text-destructive" />
            Revoke
          </DropdownMenuItem>
        )}
        <DropdownMenuItem
          onClick={() => onDelete(site.domain)}
          className="text-destructive focus:text-destructive"
        >
          <Trash2 className="h-4 w-4 mr-2" />
          Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function SitesTable({
  sites,
  isLoading,
  onApprove,
  onReject,
  onDelete,
  onRowClick,
}: {
  sites: Site[] | undefined
  isLoading: boolean
  onApprove: (domain: string) => void
  onReject: (domain: string) => void
  onDelete: (domain: string) => void
  onRowClick: (site: Site) => void
}) {
  if (isLoading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
      </div>
    )
  }

  if (!sites || sites.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <Globe className="h-12 w-12 text-muted-foreground mb-4" />
        <h3 className="text-lg font-medium">No sites yet</h3>
        <p className="text-muted-foreground">
          Sites will appear here when they install the JezPress plugin
        </p>
      </div>
    )
  }

  return (
    <TooltipProvider>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Domain</TableHead>
            <TableHead>Name</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>
              <Tooltip>
                <TooltipTrigger className="flex items-center gap-1 cursor-help">
                  OAuth Only
                </TooltipTrigger>
                <TooltipContent>
                  <p>When enabled, hides the password login form.<br />Users must sign in with Google OAuth.</p>
                </TooltipContent>
              </Tooltip>
            </TableHead>
            <TableHead>Activity</TableHead>
            <TableHead>
              <Tooltip>
                <TooltipTrigger className="flex items-center gap-1 cursor-help">
                  WP Version
                </TooltipTrigger>
                <TooltipContent>
                  <p>WordPress version cached when site was last viewed in Manage tab</p>
                </TooltipContent>
              </Tooltip>
            </TableHead>
            <TableHead>
              <Tooltip>
                <TooltipTrigger className="flex items-center gap-1 cursor-help">
                  PHP Version
                </TooltipTrigger>
                <TooltipContent>
                  <p>PHP version cached when site was last viewed in Manage tab<br />Red indicates PHP &lt; 8.0 (end of life)</p>
                </TooltipContent>
              </Tooltip>
            </TableHead>
            <TableHead className="w-[50px]"></TableHead>
          </TableRow>
        </TableHeader>
      <TableBody>
        {sites.map((site) => (
          <TableRow
            key={site.id}
            className="cursor-pointer hover:bg-muted/50"
            onClick={() => onRowClick(site)}
          >
            <TableCell>
              <div className="flex items-center gap-2">
                <Globe className="h-4 w-4 text-muted-foreground" />
                <code className="text-sm font-mono">{site.domain}</code>
              </div>
            </TableCell>
            <TableCell>{site.name || '-'}</TableCell>
            <TableCell>
              <SiteStatusBadge status={site.status} />
            </TableCell>
            <TableCell>
              {site.oauth_opt_out === 1 ? (
                <Badge variant="outline" className="text-xs border-purple-500/50 text-purple-600 dark:text-purple-400">Opted Out</Badge>
              ) : site.status === 'approved' ? (
                site.disable_password_login ? (
                  <CheckCircle className="h-5 w-5 text-green-500" />
                ) : (
                  <span className="text-muted-foreground">—</span>
                )
              ) : (
                <span className="text-muted-foreground text-sm">-</span>
              )}
            </TableCell>
            <TableCell>
              <SiteActivityBadge lastActivityAt={site.last_activity_at} status={site.status} />
            </TableCell>
            <TableCell className="text-muted-foreground">
              {site.wp_version || '—'}
            </TableCell>
            <TableCell>
              {site.php_version ? (
                <span className={
                  parseInt(site.php_version.split('.')[0] || '8') < 8
                    ? 'text-red-500 font-medium'
                    : 'text-muted-foreground'
                }>
                  {site.php_version}
                </span>
              ) : (
                <span className="text-muted-foreground">—</span>
              )}
            </TableCell>
            <TableCell onClick={(e) => e.stopPropagation()}>
              <SiteActions
                site={site}
                onApprove={onApprove}
                onReject={onReject}
                onDelete={onDelete}
              />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
      </Table>
    </TooltipProvider>
  )
}

export function SitesPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [pluginFilter, setPluginFilter] = useState<string | null>(null)
  const [oauthFilter, setOauthFilter] = useState<'all' | 'enabled' | 'disabled'>('all')
  const { data: sites, isLoading, error, refetch, isFetching } = useSites(pluginFilter)
  const { data: pluginInventory } = usePluginInventory()
  const approveSite = useApproveSite()
  const rejectSite = useRejectSite()
  const deleteSite = useDeleteSite()

  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [bulkUpdating, setBulkUpdating] = useState(false)
  const [bulkScanning, setBulkScanning] = useState(false)
  const [bulkProgress, setBulkProgress] = useState<{
    current: number
    total: number
    success: number
    failed: number
    skipped: number
    action: 'update' | 'scan'
  } | null>(null)

  // Filter sites by search query and OAuth status. Memoised so it only changes
  // when the filters do — the derived lists below then recompute once per
  // (data, filter) change instead of on every render.
  const applyFilters = useCallback((sitesToFilter: Site[]) => {
    let filtered = sitesToFilter

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase()
      filtered = filtered.filter(
        (site) =>
          site.domain.toLowerCase().includes(query) ||
          site.name?.toLowerCase().includes(query)
      )
    }

    // Filter by OAuth status (only applies to approved sites)
    if (oauthFilter !== 'all') {
      filtered = filtered.filter((site) => {
        if (site.status !== 'approved') return false
        const hasOAuthOnly = !!site.disable_password_login
        return oauthFilter === 'enabled' ? hasOAuthOnly : !hasOAuthOnly
      })
    }

    return filtered
  }, [searchQuery, oauthFilter])

  const handleApprove = async (domain: string) => {
    try {
      await approveSite.mutateAsync(domain)
      toast.success(`Site ${domain} approved`)
    } catch (err) {
      toast.error(`Failed to approve site: ${String(err)}`)
    }
  }

  const handleReject = async (domain: string) => {
    try {
      await rejectSite.mutateAsync(domain)
      toast.success(`Site ${domain} rejected`)
    } catch (err) {
      toast.error(`Failed to reject site: ${String(err)}`)
    }
  }

  const handleDelete = async () => {
    if (!deleteConfirm) return
    try {
      await deleteSite.mutateAsync(deleteConfirm)
      toast.success(`Site ${deleteConfirm} deleted`)
      setDeleteConfirm(null)
    } catch (err) {
      toast.error(`Failed to delete site: ${String(err)}`)
    }
  }

  const handleBulkScanVersions = async () => {
    if (approvedSites.length === 0) {
      toast.error('No approved sites to scan')
      return
    }

    setBulkScanning(true)
    setBulkProgress({
      current: 0,
      total: approvedSites.length,
      success: 0,
      failed: 0,
      skipped: 0,
      action: 'scan',
    })

    try {
      // Call server-side bulk scan endpoint
      const response = await apiClient.post<{
        total: number
        success: number
        failed: number
        details: Array<{ domain: string; status: string; message: string }>
        error?: string
      }>('/api/auth-server/bulk-scan/versions')

      if (response.error) {
        toast.error(`Scan failed: ${response.error}`)
        return
      }

      // Show results
      setBulkProgress({
        current: response.total,
        total: response.total,
        success: response.success,
        failed: response.failed,
        skipped: 0,
        action: 'scan',
      })

      // Show toast with summary
      toast.success(
        `Scan complete: ${response.success} sites scanned, ${response.failed} failed`
      )

      // Refresh sites list and health dashboard to show new version data
      queryClient.invalidateQueries({ queryKey: siteKeys.lists() })
      queryClient.invalidateQueries({ queryKey: ['health-summary'] })

      // Log details for debugging
      console.log('Bulk scan details:', response.details)
    } catch (error) {
      toast.error(`Scan failed: ${error instanceof Error ? error.message : 'Unknown error'}`)
    } finally {
      setBulkScanning(false)
      // Keep progress visible for a moment then clear
      setTimeout(() => setBulkProgress(null), 3000)
    }
  }

  const handleBulkUpdateJezPress = async () => {
    if (approvedSites.length === 0) {
      toast.error('No approved sites to update')
      return
    }

    setBulkUpdating(true)
    setBulkProgress({
      current: 0,
      total: approvedSites.length,
      success: 0,
      failed: 0,
      skipped: 0,
      action: 'update',
    })

    try {
      // Call server-side bulk update endpoint
      const response = await apiClient.post<{
        total: number
        success: number
        failed: number
        skipped: number
        details: Array<{ domain: string; status: string; message: string }>
        error?: string
      }>('/api/auth-server/bulk-update/jezpress')

      if (response.error) {
        toast.error(`Bulk update failed: ${response.error}`)
        return
      }

      // Show results
      setBulkProgress({
        current: response.total,
        total: response.total,
        success: response.success,
        failed: response.failed,
        skipped: response.skipped,
        action: 'update',
      })

      // Show toast with summary
      toast.success(
        `Bulk update complete: ${response.success} updated, ${response.skipped} skipped, ${response.failed} failed`
      )

      // Log details for debugging
      console.log('Bulk update details:', response.details)
    } catch (error) {
      toast.error(`Bulk update failed: ${error instanceof Error ? error.message : 'Unknown error'}`)
    } finally {
      setBulkUpdating(false)
      // Keep progress visible for a moment then clear
      setTimeout(() => setBulkProgress(null), 3000)
    }
  }

  // Navigate to site detail page
  const handleSiteClick = (site: Site) => {
    navigate(`/dashboard/jezpress/${encodeURIComponent(site.domain)}`)
  }

  // Split by status (unfiltered — bulk ops + the pending banner act on these).
  const pendingSites = useMemo(() => sites?.filter((s) => s.status === 'pending') || [], [sites])
  const approvedSites = useMemo(() => sites?.filter((s) => s.status === 'approved') || [], [sites])
  const rejectedSites = useMemo(() => sites?.filter((s) => s.status === 'rejected') || [], [sites])

  // Search/OAuth-filtered views for the tabs — computed once per (data, filter)
  // change and reused for both the tab counts and the table bodies.
  const filteredAll = useMemo(() => applyFilters(sites || []), [applyFilters, sites])
  const filteredApproved = useMemo(() => applyFilters(approvedSites), [applyFilters, approvedSites])
  const filteredPending = useMemo(() => applyFilters(pendingSites), [applyFilters, pendingSites])
  const filteredRejected = useMemo(() => applyFilters(rejectedSites), [applyFilters, rejectedSites])

  if (error) {
    const message = error instanceof Error ? error.message : String(error)
    return (
      <Card>
        <CardContent className="p-6 space-y-4">
          <div className="space-y-1">
            <p className="text-destructive font-medium">Couldn't load sites</p>
            <p className="text-sm text-muted-foreground">{message}</p>
            <p className="text-sm text-muted-foreground">
              The auth server may have been waking up. This usually clears in a moment — try again.
            </p>
          </div>
          <Button onClick={() => refetch()} disabled={isFetching} variant="outline">
            {isFetching ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Retrying...
              </>
            ) : (
              'Try again'
            )}
          </Button>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-4 md:space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">OAuth Sites</h1>
          <p className="text-muted-foreground">
            Manage WordPress sites with the JezPress OAuth plugin
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            onClick={handleBulkScanVersions}
            disabled={bulkScanning || bulkUpdating || approvedSites.length === 0}
            variant="outline"
          >
            {bulkScanning ? (
              <>
                <Loader2 className="h-4 w-4 sm:mr-2 animate-spin" />
                <span className="hidden sm:inline">Scanning...</span>
              </>
            ) : (
              <>
                <ScanSearch className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Scan All Sites</span>
              </>
            )}
          </Button>
          <Button
            onClick={handleBulkUpdateJezPress}
            disabled={bulkUpdating || bulkScanning || approvedSites.length === 0}
            variant="outline"
          >
            {bulkUpdating ? (
              <>
                <Loader2 className="h-4 w-4 sm:mr-2 animate-spin" />
                <span className="hidden sm:inline">Updating...</span>
              </>
            ) : (
              <>
                <ArrowUpCircle className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Update All JezPress</span>
              </>
            )}
          </Button>
          <Button asChild>
            <a href="/api/download/jezpress.zip" download>
              <Download className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Download Plugin</span>
            </a>
          </Button>
        </div>
      </div>

      {/* Bulk progress display (scan or update) */}
      {(bulkUpdating || bulkScanning || bulkProgress) && (
        <Card className="border-blue-500/50 bg-blue-500/5">
          <CardContent className="py-4">
            <div className="flex items-center justify-between text-sm">
              {(bulkUpdating || bulkScanning) && bulkProgress?.current === 0 ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {bulkProgress?.action === 'scan'
                    ? `Scanning ${bulkProgress.total} sites for version info... (runs on server, you can close this page)`
                    : `Updating JezPress on ${bulkProgress?.total} sites... (runs on server, you can close this page)`}
                </span>
              ) : (
                <span>
                  {bulkUpdating || bulkScanning
                    ? 'Processing...'
                    : bulkProgress?.action === 'scan'
                      ? 'Scan complete:'
                      : 'Update complete:'}
                </span>
              )}
              {bulkProgress && bulkProgress.current > 0 && (
                <div className="flex items-center gap-4 text-xs">
                  <span className="text-green-500">
                    {bulkProgress.success} {bulkProgress.action === 'scan' ? 'scanned' : 'updated'}
                  </span>
                  {bulkProgress.action === 'update' && (
                    <span className="text-muted-foreground">
                      {bulkProgress.skipped} skipped
                    </span>
                  )}
                  <span className="text-red-500">{bulkProgress.failed} failed</span>
                </div>
              )}
            </div>
            {bulkProgress && bulkProgress.current > 0 && (
              <div className="mt-2 h-2 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full bg-green-500 transition-all"
                  style={{
                    width: `${(bulkProgress.current / bulkProgress.total) * 100}%`,
                  }}
                />
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Pending approval banner */}
      {pendingSites.length > 0 && (
        <Card className="border-yellow-500/50 bg-yellow-500/5">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Clock className="h-4 w-4 text-yellow-500" />
              {pendingSites.length} site{pendingSites.length > 1 ? 's' : ''} awaiting approval
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {pendingSites.map((site) => (
                <div
                  key={site.id}
                  className="flex items-center gap-2 rounded-lg border bg-background px-3 py-2"
                >
                  <code className="text-sm">{site.domain}</code>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-6 px-2 text-green-500 hover:text-green-600 hover:bg-green-500/10"
                    onClick={() => handleApprove(site.domain)}
                    disabled={approveSite.isPending}
                  >
                    <CheckCircle className="h-4 w-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-6 px-2 text-destructive hover:text-destructive hover:bg-destructive/10"
                    onClick={() => handleReject(site.domain)}
                    disabled={rejectSite.isPending}
                  >
                    <XCircle className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Search, Plugin Filter, and Sites table with tabs */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4 mb-4">
        <div className="relative flex-1 sm:max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search sites..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>

        {/* OAuth filter dropdown */}
        <Select
          value={oauthFilter}
          onValueChange={(value) => setOauthFilter(value as 'all' | 'enabled' | 'disabled')}
        >
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="OAuth Only" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All OAuth</SelectItem>
            <SelectItem value="enabled">OAuth Only: On</SelectItem>
            <SelectItem value="disabled">OAuth Only: Off</SelectItem>
          </SelectContent>
        </Select>

        {/* Plugin filter dropdown */}
        <div className="flex items-center gap-2">
          <Select
            value={pluginFilter || 'all'}
            onValueChange={(value) => setPluginFilter(value === 'all' ? null : value)}
          >
            <SelectTrigger className="w-[200px]">
              <Package className="h-4 w-4 mr-2 text-muted-foreground" />
              <SelectValue placeholder="Filter by plugin" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All sites</SelectItem>
              {pluginInventory?.map((plugin) => (
                <SelectItem key={plugin.slug} value={plugin.slug}>
                  {plugin.name} ({plugin.site_count})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {pluginFilter && (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => setPluginFilter(null)}
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>

        {(searchQuery || pluginFilter || oauthFilter !== 'all') && (
          <span className="text-sm text-muted-foreground">
            {filteredAll.length} results
          </span>
        )}
      </div>

      <Tabs defaultValue="all">
        <TabsList>
          <TabsTrigger value="all">All ({filteredAll.length})</TabsTrigger>
          <TabsTrigger value="approved">Approved ({filteredApproved.length})</TabsTrigger>
          <TabsTrigger value="pending">Pending ({filteredPending.length})</TabsTrigger>
          <TabsTrigger value="rejected">Rejected ({filteredRejected.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>All Sites</CardTitle>
              <CardDescription>
                {isLoading ? 'Loading...' : `${filteredAll.length} registered sites`}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <SitesTable
                sites={filteredAll}
                isLoading={isLoading}
                onApprove={handleApprove}
                onReject={handleReject}
                onDelete={setDeleteConfirm}
                onRowClick={handleSiteClick}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="approved" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Approved Sites</CardTitle>
              <CardDescription>
                Sites that can use Jezweb OAuth login
              </CardDescription>
            </CardHeader>
            <CardContent>
              <SitesTable
                sites={filteredApproved}
                isLoading={isLoading}
                onApprove={handleApprove}
                onReject={handleReject}
                onDelete={setDeleteConfirm}
                onRowClick={handleSiteClick}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="pending" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Pending Sites</CardTitle>
              <CardDescription>
                Sites awaiting approval
              </CardDescription>
            </CardHeader>
            <CardContent>
              <SitesTable
                sites={filteredPending}
                isLoading={isLoading}
                onApprove={handleApprove}
                onReject={handleReject}
                onDelete={setDeleteConfirm}
                onRowClick={handleSiteClick}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="rejected" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Rejected Sites</CardTitle>
              <CardDescription>
                Sites that have been denied access
              </CardDescription>
            </CardHeader>
            <CardContent>
              <SitesTable
                sites={filteredRejected}
                isLoading={isLoading}
                onApprove={handleApprove}
                onReject={handleReject}
                onDelete={setDeleteConfirm}
                onRowClick={handleSiteClick}
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Site</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{deleteConfirm}</strong>?
              This will remove the site from the registry and they will need to
              re-register to use Jezweb OAuth.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteSite.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
